<script setup lang="ts">
import type { ShipCompareShip } from "~~/types/shipexkurs";
import type { ComponentSuperGroup } from "~/utils/build-ship-component-groups";
import type { ShipexkursIconName } from "~/utils/shipexkurs-icons";

const props = defineProps<{ ships: ShipCompareShip[] }>();

/** Wie `ships/[id].vue`s `shipLabel` — für generische Namen ohne Produktbezeichnung (z.B. Frachtraster). */
function shipLabelFor(ship: ShipCompareShip): string {
  const manufacturer = ship.summary.manufacturer;
  const code = manufacturer?.code || manufacturer?.short || "";
  const titledCode = code ? code.charAt(0).toUpperCase() + code.slice(1).toLowerCase() : "";
  return [titledCode, ship.summary.name].filter(Boolean).join(" ");
}

const superGroupsByShipId = computed(() => {
  const map = new Map<string, ComponentSuperGroup[]>();
  for (const ship of props.ships) {
    map.set(ship.id, buildComponentSuperGroups(ship.raw?.ports ?? [], ship.items, shipLabelFor(ship)));
  }
  return map;
});

interface RenderSubGroup {
  label: string;
  icon: ShipexkursIconName;
}

interface RenderSuperGroup {
  label: string;
  subGroups: RenderSubGroup[];
}

/**
 * Vereinigungsmenge aller bei mind. einem Schiff vorkommenden Kategorien, in
 * der bestehenden `COMPONENT_COLUMNS`-Kuratierung (siehe `get-component-group.ts`) —
 * dieselbe Reihenfolge wie im Einzelschiff-Komponentenbaum, nur nicht auf 3
 * Layout-Spalten verteilt, sondern als durchlaufende Sektionsliste.
 */
const renderSuperGroups = computed<RenderSuperGroup[]>(() => {
  const groups: RenderSuperGroup[] = [];

  for (const label of COMPONENT_COLUMNS.flat()) {
    const subGroups = new Map<string, ShipexkursIconName>();
    let present = false;

    for (const ship of props.ships) {
      const superGroup = superGroupsByShipId.value.get(ship.id)?.find((group) => group.label === label);
      if (!superGroup) continue;
      present = true;
      for (const subGroup of superGroup.subGroups) {
        if (!subGroups.has(subGroup.label)) subGroups.set(subGroup.label, subGroup.icon);
      }
    }

    if (present) {
      groups.push({ label, subGroups: [...subGroups.entries()].map(([subLabel, icon]) => ({ label: subLabel, icon })) });
    }
  }

  return groups;
});

function rowsFor(shipId: string, superLabel: string, subLabel: string) {
  return superGroupsByShipId.value.get(shipId)
    ?.find((group) => group.label === superLabel)
    ?.subGroups.find((sub) => sub.label === subLabel)
    ?.rows ?? [];
}

// Die Spalten wachsen wie die Spalten der Datentabelle mit dem verfügbaren
// Platz, unterschreiten aber nie ihre lesbare Mindestbreite. Reicht der Platz
// nicht, scrollt die gesamte Sektion horizontal, sodass alle Kategorie-Karten
// dieselbe Spaltenposition behalten.
const COLUMN_WIDTH_PX = 320;
const COLUMN_GAP_PX = 12;
const CARD_HORIZONTAL_PADDING_PX = 48;
const contentWidth = computed(() =>
  props.ships.length * COLUMN_WIDTH_PX
  + Math.max(0, props.ships.length - 1) * COLUMN_GAP_PX
  + CARD_HORIZONTAL_PADDING_PX,
);

const { containerRef, isDragging, onPointerDown, onPointerMove, onPointerUp, onClickCapture } = useDragScroll();
</script>

<template>
  <div v-if="renderSuperGroups.length">
    <div
      ref="containerRef"
      class="overflow-x-auto pb-1 select-none"
      :class="isDragging ? 'cursor-grabbing' : 'cursor-grab'"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @click.capture="onClickCapture"
    >
      <div class="space-y-4" :style="{ width: `${contentWidth}px`, minWidth: '100%' }">
        <UCard
          v-for="superGroup in renderSuperGroups"
          :key="superGroup.label"
          variant="outline"
          class="border border-default bg-bunker-950 ring-0"
        >
          <template #header>
            <h3
              :id="getComponentCategoryAnchorId(superGroup.label)"
              class="!my-0 scroll-mt-16 text-sm font-semibold uppercase tracking-wide text-aris-400"
            >
              {{ superGroup.label }}
            </h3>
          </template>

          <div class="space-y-5">
            <div v-for="subGroup in superGroup.subGroups" :key="subGroup.label">
              <p class="mb-2 flex items-center gap-1.5 text-[0.65rem] font-medium text-neutral-500 uppercase tracking-wide">
                <ShipexkursSystemIcon :name="subGroup.icon" class="size-3.5 shrink-0" />
                {{ subGroup.label }}
              </p>
              <div class="flex gap-3">
                <div
                  v-for="ship in ships"
                  :key="ship.id"
                  class="min-w-80 flex-1 basis-0 space-y-1.5 border-l-2 pl-2"
                  :style="{ borderColor: ship.color }"
                >
                  <div v-if="ship.itemsPending" class="flex items-center gap-1.5 text-xs text-neutral-500">
                    <UIcon name="i-lucide-loader-2" class="size-3.5 animate-spin" />
                    Lädt…
                  </div>
                  <template v-else-if="rowsFor(ship.id, superGroup.label, subGroup.label).length">
                    <ShipexkursComponentRowItem
                      v-for="row in rowsFor(ship.id, superGroup.label, subGroup.label)"
                      :key="row.key"
                      :row="row"
                    />
                  </template>
                  <span v-else class="text-xs text-dimmed">–</span>
                </div>
              </div>
            </div>
          </div>
        </UCard>
      </div>
    </div>
  </div>
  <p v-else class="text-sm text-neutral-500">Keine Ausrüstungsdaten verfügbar.</p>
</template>
